import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import {
  useDuplicateSectionCeleryMutation,
  usePatchAhuUnitMutation,
} from 'app/services/ahuBoardFormsApi';
import type { IAhuBoardSection, IAhuBoardTunnel, IAhuBoardUnit, ICmsAhuDoc } from 'models';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import { clearSelection, setAhuTabAccordion } from 'app/slices/ahuBoardSlice';
import {
  getPossibleBindingsForInsertion,
  type InsertionBinding,
} from './insertionBindings';
import { getPossibleHybridBindings } from './hybridInsertionBindings';
import {
  alignmentSpanForNewChain,
  applySign,
  defaultOffsetForNewTunnel,
  getTunnelPhysicalSpan,
  offsetForPhysicalSpan,
  type XOffsetSign,
} from './hybridXOffset';
import { awaitSectionWrites, beginSectionWrites } from '../pendingSectionWrites';
import {
  DEFAULT_SECTION_LENGTH,
  createLugsForNewTunnel,
  getOrCreateHybridTunnel,
  getOrCreateTunnelFromIndex,
  refetchLugPlacementsAfterGeometryChange,
  type HybridTunnelTarget,
} from '../boardActions';
import { validateTunnelOnlyComponents } from '../validation/validationController';
import { holdCanvasRedrawBatchFor } from '../three/canvasRedrawScheduler';
import { analyticsComponentName, track } from 'app/analytics';
import { numberKeypressFilter } from 'helpers/numberKeypressFilter';

/**
 * What the X-Offset box may hold: digits with at most one decimal point, no
 * sign. The empty string and a lone/trailing '.' are in, because they are
 * states you type THROUGH on the way to a number — '12.' is one keystroke
 * short of '12.5', and rejecting it makes the point unkeyable.
 *
 * Unsigned on purpose: the field carries a magnitude and the column header's
 * '+ / −' toggle carries the sign (`renderSignToggle`), which is also why the
 * input declares `data-min="0"` and `onKeyDown` already swallowed a typed
 * '+'/'-'. A negative here would be read back through `Math.abs` anyway.
 */
const UNSIGNED_DECIMAL = /^\d*\.?\d*$/;

/**
 * The DIRECTION column's two options, verbatim from
 * insert_component.popup.tmpl:86-89 (single tunnel — arrow AFTER the label,
 * separated by an EN SPACE) and :186-189/:192-195 (dual tunnel — a plain
 * arrow BEFORE the label). They differ between the two layouts in legacy and
 * are kept that way here.
 *
 * The single-tunnel arrows are the Material Symbols `arrow_forward` /
 * `arrow_back` codepoints rather than U+27A1/U+2B05, and the stylesheet
 * appends "Material Symbols Outlined" to these selects' font stack so they
 * resolve (`.row-airflow-select` in ahuBoardOverrides.scss). The icon font
 * react.html already loads covers the full em box, so the arrow is drawn at
 * the same size and on the same baseline as the label in BOTH states, closed
 * control and open option list.
 *
 * What they replace: U+27A1/U+2B05, which are in no font we ship, so each one
 * fell back to whatever the OS had and drew ~8px of ink against a 13px label.
 * That is what read as "too small", and a `<select>` cannot size one character
 * of its own label, so the fix is the glyph rather than CSS.
 *
 * Dual keeps its inline U+2192/U+2190, which Roboto does have.
 */
const AIRFLOW_DIRECTIONS = [
  { id: 1, single: 'Left-to-Right \u2002\uE5C8', dual: '\u2192 Left-to-Right' },
  { id: 2, single: 'Right-to-Left \u2002\uE5C4', dual: '\u2190 Right-to-Left' },
];

/** The other of the two `tunnel_airflow_directions` rows (1 = LTR, 2 = RTL). */
const oppositeDirection = (id: number): number => (Number(id) === 2 ? 1 : 2);

/**
 * How a dual-tunnel component's two tunnels must relate on airflow, keyed by
 * `MasterAHUComponent.value` (`er_wheel` is Heat Wheel \u2014 the Q22 naming split,
 * see boardActions' COMPONENT_PLURALS; `energy_wheel`/`rotary` are the derived
 * type names the same component is read back as, accepted here so a caller that
 * uses one of them gets the rule too).
 *
 * - **`opposite`** \u2014 a Heat Wheel transfers between two COUNTER-flowing
 *   streams: supply one way, exhaust the other. The two sections it links can
 *   never run the same way, so the dialog keeps the pair locked rather than
 *   letting a user build a unit that cannot exist (requested 2026-10-07).
 * - **`same`** \u2014 a Plate Heatex runs both streams in parallel
 *   (`dual_tunnel_air_rel_dirs`), and the server enforces it on create:
 *   `plate_exchanger_viewset.py:142-145` copies section 1's tunnel's airflow
 *   onto section 2's and saves it. Locking the pair here is that same rule
 *   shown up front \u2014 without it the dialog accepted a counter-flow Plate, sent
 *   an X-Offset computed for it, and the server then flipped the second tunnel
 *   back WITHOUT re-basing that offset, which slid the tunnel by its own
 *   length on the canvas.
 *
 * The relation is enforced on the PRIMARY (bottom/left) tunnel's direction:
 * column 1 is always derived from column 0, so there is one source of truth
 * and the X-Offset math \u2014 which follows column 0 \u2014 cannot disagree with it.
 */
const DUAL_AIRFLOW_RELATION: Record<string, 'opposite' | 'same'> = {
  er_wheel: 'opposite',
  energy_wheel: 'opposite',
  rotary: 'opposite',
  plate_heat_exchanger: 'same',
  plate: 'same',
};

/**
 * Phase 6e1 — Insert/Duplicate Section popup (§9.2, insert_component.popup.js
 * + insert_component.popup.tmpl, markup faithful). ONE React popup root
 * ([NEG]#42 — no duplicate-id body append). Single + dual layouts, rows from
 * the §19.3 bindings builder, first row pre-chosen, INSERT gated until a row
 * is chosen, dblclick = INSERT, Escape = cancel.
 *
 * INSERT decodes orders per the legacy select math (selected option index →
 * `order = idx + 2`, Upstream Edge = 1) and fires `create:component:any`
 * (ctrl passthrough); duplicate mode POSTs /ahu/duplicate/section/ (§20.10
 * #104 — completion via syncdata/polling, refetch-driven here).
 */

export interface InsertPopupRequest {
  name: string;
  value: string;
  type: string; // 'single_tunnel' | 'dual_tunnel'
  duplicate?: boolean;
  sectionId?: number;
}

interface InsertSectionPopupProps {
  request: InsertPopupRequest;
  ahu: IAhuBoardUnit;
  tunnels: IAhuBoardTunnel[];
  sections: IAhuBoardSection[];
  cms: ICmsAhuDoc | undefined;
  isPowerUser: boolean;
  onClose: () => void;
}

const InsertSectionPopup = ({
  request,
  ahu,
  tunnels,
  sections,
  cms,
  isPowerUser,
  onClose,
}: InsertSectionPopupProps) => {
  const dispatch = useAppDispatch();
  const [duplicateSection] = useDuplicateSectionCeleryMutation();
  const [patchAhuUnit] = usePatchAhuUnitMutation();
  const [chosen, setChosen] = useState(0);
  const [orders, setOrders] = useState<Record<string, number>>({});

  const isDual = request.type === 'dual_tunnel';

  /** The section Duplicate Section was opened on, and the tunnel holding it. */
  const duplicateSource = useMemo(
    () =>
      request.duplicate && request.sectionId != null
        ? sections.find((s) => s.id === request.sectionId)
        : undefined,
    [request.duplicate, request.sectionId, sections]
  );
  const duplicateSourceTunnel = useMemo(
    () =>
      duplicateSource ? tunnels.find((t) => t.id === duplicateSource.tunnel_id) : undefined,
    [duplicateSource, tunnels]
  );

  // Standard/Hybrid mode (§4 insert_component.popup.js `initialize`).
  const hasAnySections = sections.length > 0;
  /**
   * `is_hybrid` is the choice made when the FIRST section went in, so an AHU
   * with no sections has not chosen yet — whatever it was before every section
   * was deleted off it. The server clears the flag on the delete paths
   * (api/rest_ahu/utils.py `reset_hybrid_mode_if_empty`); reading it as
   * "not chosen" here as well means the toggle comes back whether or not the
   * unit refetch has landed yet.
   */
  const modeChosen = hasAnySections && !!ahu.is_hybrid;
  /**
   * Which modes this dialog offers. Mode is a property of the AHU, not of the
   * gesture: legacy computes `show_standard_mode` / `show_hybrid_mode` from
   * `is_hybrid` and the section count alone, with no reference to
   * `self.duplicate` (insert_component.popup.js:86-123), so Duplicate Section
   * renders the SAME layout Add Section does on the same unit.
   *
   * React gated both on `!request.duplicate`, which forced Duplicate through
   * the standard GRID layout on a hybrid AHU — a unit that has no grid. Every
   * hybrid tunnel carries the same legacy `index_id` (the unit's
   * configuration value is the word "hybrid", not "X_Y"), so the dialog read
   * "ADD IN TUNNEL 1_1 … RESULTING AHU CONFIGURATION", only the first
   * tunnel's sections were offered under "ADD DOWNSTREAM OF", and
   * `getOrCreateTunnelFromIndex` resolved every one of those rows to whichever
   * tunnel happened to hold that index — so the copy landed in the chain's
   * lead tunnel however the dialog was answered, or created an unaddressed
   * tunnel (no row/level/position/x_offset) for a "2_1"-style row, which the
   * hybrid layout then has nowhere to put.
   *
   * The `isPowerUser && !hasAnySections` half is the bootstrap case — choosing
   * hybrid for a unit that has not placed its first section yet. It cannot be
   * reached in duplicate mode (the section being duplicated IS a section), so
   * Duplicate gets hybrid exactly when the unit is already hybrid, and the
   * Standard/Hybrid pill below — rendered only when BOTH modes apply — never
   * appears on it.
   */
  const showStandardMode = !modeChosen;
  const showHybridMode = modeChosen || (isPowerUser && !hasAnySections);
  /**
   * `self.mode = self.show_hybrid_mode ? "hybrid" : "standard"`
   * (insert_component.popup.js:123) — Hybrid wins whenever it is on offer, so
   * a Power User opening this on a still-empty non-hybrid AHU lands on Hybrid,
   * not Standard. Gating on `ahu.is_hybrid` alone left exactly that case on
   * Standard.
   */
  const [mode, setMode] = useState<'standard' | 'hybrid'>(
    showHybridMode ? 'hybrid' : 'standard'
  );
  const isHybridMode = showHybridMode && (mode === 'hybrid' || !showStandardMode);

  /**
   * The row the user has clicked, if any. `null` is "nobody has chosen yet",
   * which is NOT the same as row 0 in duplicate mode — see `hybridChosen`.
   */
  const [hybridChosenRow, setHybridChosenRow] = useState<number | null>(null);
  const [hybridDirections, setHybridDirections] = useState<Record<string, number>>({});
  /**
   * Typed X-Offset overrides, keyed `rowIdx-column`, held as the raw string so
   * a half-typed "12." survives a keystroke. A row with no entry shows its
   * computed default — and legacy's `applyXOffsetSignToAllRows` OVERWRITES
   * every input whenever the sign or a direction changes, so those handlers
   * clear the overrides rather than trying to preserve them.
   */
  const [hybridOffsets, setHybridOffsets] = useState<Record<string, string>>({});
  /** `self.xOffsetSign` — ONE toggle beside the column header, not per row. */
  const [xOffsetSign, setXOffsetSign] = useState<XOffsetSign>('plus');

  const sectionsByTunnel = useMemo(() => {
    const map: Record<number, IAhuBoardSection[]> = {};
    tunnels.forEach((t) => {
      map[t.id] = sections
        .filter((s) => s.tunnel_id === t.id)
        .sort((a, b) => (a.order_rate ?? 0) - (b.order_rate ?? 0));
    });
    return map;
  }, [tunnels, sections]);

  /**
   * A hybrid row's defaults are CHAIN GEOMETRY, so they must not be committed
   * from a cache a previous insert is still writing into.
   *
   * Every hybrid row's X-Offset default butts the new tunnel against the
   * chain's current end (`defaultXOffset`), and the `position` the create
   * sends counts the peers it can see (`getOrCreateHybridTunnel`). Both read
   * the `getAhuTunnelList` cache, and an insert updates that cache twice: once
   * right after its tunnel POST — where the new tunnel is real but still
   * SECTIONLESS, so it has no footprint and does not move the chain's end —
   * and again once the section has landed. Reopened in between (one insert
   * after another, which is how a hybrid unit gets built), the dialog read the
   * first of those and offered the offset it had JUST used: the new section
   * landed exactly on top of the previous one, and since
   * `_auto_address_hybrid_peers` breaks an exact tie by newest-first, it was
   * also numbered AHEAD of the section it was added after (reported
   * 2026-10-08: "the second section must be positioned after the first").
   *
   * So wait for the section writes to commit (`pendingSectionWrites`), then
   * re-read the list from the server — and hold INSERT until that lands, so
   * nothing can be committed off a mid-insert cache however fast the user is
   * (the `non-clickable` class stops the click, the guard in `insertHybrid`
   * stops the Enter the popup's autofocus invites). The rows themselves stay
   * on screen throughout: they are only ever one GET behind, and a dialog that
   * blanks for the second or two a loaded box takes to answer reads as broken.
   */
  const [chainsFresh, setChainsFresh] = useState(false);
  useEffect(() => {
    if (!isHybridMode) return undefined;
    let alive = true;
    void (async () => {
      await awaitSectionWrites();
      await dispatch(
        ahuBoardApi.endpoints.getAhuTunnelList.initiate(ahu.id, {
          forceRefetch: true,
          subscribe: false,
        })
      )
        .unwrap()
        // A failed re-read is not a reason to refuse the insert — fall back to
        // the cache the board is already drawing from, as before this change.
        .catch(() => undefined);
      if (alive) setChainsFresh(true);
    })();
    return () => {
      alive = false;
    };
  }, [isHybridMode, ahu.id, dispatch]);

  const hybridBindings = useMemo(
    () =>
      isHybridMode
        ? getPossibleHybridBindings({ isDualTunnel: isDual, tunnels, sectionsByTunnel, cms })
        : [],
    [isHybridMode, isDual, tunnels, sectionsByTunnel, cms]
  );

  /**
   * The row holding the SOURCE section's own `row:level`, when this is a
   * duplicate — the address the copy belongs at unless the user says otherwise.
   *
   * A hybrid duplicate makes a new tunnel (every hybrid row does), so the only
   * thing carrying the source tunnel's configuration over to the copy is which
   * row the dialog is answered with. Defaulting to row 0 like Add Section does
   * put the copy of a 1:2 section at `1:1` — a different level, and with level
   * 1's airflow rather than its own, so duplicating an upper-level section
   * silently moved it down a storey and turned it around.
   *
   * `null` when the source tunnel is not on the list (it always is in practice)
   * or when this is not a duplicate; both fall back to row 0 below.
   */
  const duplicateSourceRow = useMemo(() => {
    if (!duplicateSourceTunnel) return null;
    const row = duplicateSourceTunnel.row ?? 1;
    const level = duplicateSourceTunnel.level ?? 1;
    const idx = hybridBindings.findIndex(
      (r) => r.targets.length === 1 && r.targets[0].row === row && r.targets[0].level === level
    );
    return idx >= 0 ? idx : null;
  }, [duplicateSourceTunnel, hybridBindings]);

  /**
   * `self.chosen_index` — the row INSERT acts on: the one the user clicked,
   * else the source section's own row for a duplicate, else the first.
   */
  const hybridChosen = hybridChosenRow ?? duplicateSourceRow ?? 0;

  /**
   * The direction a duplicate's own row defaults to: the SOURCE tunnel's, so
   * the copy runs the way the section it was copied from runs. Only its own
   * row — pick another `row:level` deliberately and that level's own
   * convention (`defaultAirflowDirectionId`) is the better default, exactly as
   * for an Add Section there.
   *
   * Column 0 only: a dual-tunnel section cannot be duplicated at all
   * (SectionToolbarChrome refuses it), so a duplicate's row never has a second.
   */
  const duplicateSourceDirection = (
    bindingIdx: number,
    column: number
  ): number | undefined => {
    if (bindingIdx !== duplicateSourceRow || column !== 0) return undefined;
    const dir = Number(duplicateSourceTunnel?.airflow_direction_id);
    return dir === 1 || dir === 2 ? dir : undefined;
  };

  const bindings = useMemo(
    () =>
      getPossibleBindingsForInsertion({
        isDualTunnel: isDual,
        tunnels,
        sectionsByTunnel,
        cms,
      }),
    [isDual, tunnels, sectionsByTunnel, cms]
  );

  // Escape = cancel (§9.2).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keyup', onKey);
    return () => document.removeEventListener('keyup', onKey);
  }, [onClose]);

  const selectedOrder = (binding: InsertionBinding, column: number): number => {
    const key = `${bindings.indexOf(binding)}-${column}`;
    if (key in orders) return orders[key];
    // defaults to last (§9.2).
    return binding.occupied_tunnel_sections_last_index[column] ?? -1;
  };

  const noteBusy = (on: boolean) =>
    document
      .querySelector('.duplicating-section-note')
      ?.classList[on ? 'add' : 'remove']('active');

  /**
   * `section.duplicate` (section/model/section.js:451-520) from the POST
   * onwards — everything the two duplicate paths below share once the target
   * tunnel has been resolved.
   *
   * The two flags that tell `SectionDuplicateView` to carry the SOURCE
   * tunnel's settings onto the tunnel the copy landed in
   * (ahu/duplicate/tunnel_config.py):
   *
   * - `copyTunnelShell` is legacy's `new_tunnel: !tunnel.is_not_new` — the
   *   standard-AHU set (width/height/base/lug attachment + the two casing and
   *   two floor materials), sent only when the resolve had to create the
   *   tunnel. See `runHybridDuplicate` for why the hybrid path sends it false.
   * - `copyTunnelConfig` is the hybrid set: the whole casing and floor rows,
   *   the five Walls/Roof face flags and the lifting-lug attachment. Nothing
   *   positional — the address, airflow, X-Offset, Y-Offset and size are the
   *   ones the hybrid create resolved for where the copy landed.
   */
  const duplicateIntoTunnel = async (
    source: IAhuBoardSection,
    resolved: { tunnel: IAhuBoardTunnel; created: boolean },
    opts: {
      order: number;
      copyTunnelShell: boolean;
      copyTunnelConfig?: boolean;
      /**
       * Called once the copy is in the tunnel — or once the poll has given up
       * on it. The POST is not that moment: the duplicate runs on celery and
       * answers before the section exists, which is why this polls at all. So
       * a caller holding a pending-section-write window has to close it here
       * (pendingSectionWrites.ts), not when the POST resolves.
       */
      onSectionsWritten?: () => void;
    }
  ) => {
    const targetTunnelId = resolved.tunnel.id;
    // Sections already in the target BEFORE the duplicate — the poll below
    // identifies the new row as the one that is not in this set (legacy's
    // `ids` closure). A tunnel just created is empty, so any row is fresh.
    const knownIds = new Set((sectionsByTunnel[targetTunnelId] ?? []).map((s) => s.id));
    try {
      await duplicateSection({
        section_id: source.id,
        tunnel_id: targetTunnelId,
        order_rate: opts.order,
        new_tunnel: opts.copyTunnelShell,
        copy_tunnel_config: opts.copyTunnelConfig,
      }).unwrap();
    } catch {
      /* server refused — the poll below still reconciles, as legacy did */
    }
    if (resolved.created && resolved.tunnel.base_id) {
      void dispatch(createLugsForNewTunnel(targetTunnelId));
    }

    // §20.10 #104 — completion via syncdata action.fetch; the client
    // ALSO polls until the new section appears (legacy nested
    // 500/300 ms retry timers — bounded refetch loop here).
    let tries = 0;
    const poll = () => {
      tries += 1;
      dispatch(
        ahuBoardApi.endpoints.getTunnelSectionList.initiate(targetTunnelId, {
          forceRefetch: true,
        })
      )
        .unwrap()
        .then((rows) => {
          const fresh = rows.find((r) => !knownIds.has(r.id));
          if (fresh) {
            noteBusy(false);
            opts.onSectionsWritten?.();
            track('section_duplicated', { section_type: analyticsComponentName(source.type) });
            // Tunnel length recalced server-side by the duplicate —
            // refetch so the canvas props cascade sees it (legacy gets
            // this via the tunnel syncdata socket). Not awaited, but the
            // canvas pass below is held for it (canvasRedrawScheduler), so a
            // duplicate lands in ONE draw carrying the grown tunnel rather
            // than drawing the section and then re-drawing the tunnel.
            holdCanvasRedrawBatchFor(
              dispatch(
                ahuBoardApi.endpoints.getAhuTunnelList.initiate(ahu.id, {
                  forceRefetch: true,
                })
              )
            );
            // The duplicate grew the target tunnel, so its lug placement has
            // moved — re-read it, as the insert path does. On hybrid the
            // server re-laid the runs in this same request; on a standard AHU
            // the re-lay happens inside the placement GET itself
            // (refetchLugPlacementsAfterGeometryChange).
            holdCanvasRedrawBatchFor(
              dispatch(refetchLugPlacementsAfterGeometryChange(ahu.id, [targetTunnelId]))
            );
            ahuBoardBus.emit('added:new:component', fresh);
            ahuBoardBus.emit('show:on:canvas', fresh);
            ahuBoardBus.emit('select:section', { section: { id: fresh.id } });
            // §13 row 3 for the duplicate — open the AHU tab's accordion on
            // the tunnel the copy landed in, so the Data Bar follows the
            // selection the emit above just made (and, on a hybrid AHU,
            // TabAhu's effect on the resolved accordion expands the enclosing
            // "Row:level" group).
            //
            // The `select:section` emit cannot do it here, even though it
            // normally does: AhuCanvas resolves the owning tunnel out of its
            // own `tunnelsRef`, which is React state one commit behind the
            // refetch that just produced `fresh` — so for the one select that
            // matters most it finds no row, leaves `rowTunnelId` null and
            // skips the accordion. We know the target tunnel for certain, so
            // dispatch it directly; it runs after the emit, which means it
            // also wins when the canvas HAS caught up and writes the same id.
            dispatch(setAhuTabAccordion(`tunnel:${targetTunnelId}`));
            ahuBoardBus.emit('redraw:canvas');
            // `ahu.validateTunnelOnlyComponents({tunnelId})` (section.js:508) —
            // the duplicate changes the target tunnel's component set, and on a
            // freshly created tunnel it is the only thing that seeds its errors.
            void dispatch(validateTunnelOnlyComponents({ tunnelId: targetTunnelId }));
          } else if (tries < 12) {
            window.setTimeout(poll, tries < 3 ? 500 : 1000);
          } else {
            noteBusy(false);
            opts.onSectionsWritten?.();
          }
        })
        .catch(() => {
          if (tries < 12) window.setTimeout(poll, 1000);
          else opts.onSectionsWritten?.();
        });
    };
    window.setTimeout(poll, 500);
  };

  /**
   * Standard-AHU duplicate. The target tunnel is whatever the chosen row's
   * tunnel INDEX resolves to — and legacy resolves it through
   * `api.tunnel.get_or_create_tunnel_from_index`, which CREATES the tunnel when
   * the AHU has none at that index yet.
   *
   * That resolve is what was missing. React looked the index up in the tunnels
   * it already had and, on a miss, fell back to `source.tunnel_id` — so every
   * duplicate aimed at a tunnel that did not exist yet (the common case: the
   * popup lists indexes the resulting configuration allows, not just live
   * tunnels) landed back in the section's OWN tunnel, which is the reported bug.
   */
  const runDuplicate = async (
    source: IAhuBoardSection,
    block: { tunnel_index_id: number; order: number }
  ) => {
    noteBusy(true);
    const resolved = await dispatch(
      getOrCreateTunnelFromIndex(block.tunnel_index_id, { ahuId: ahu.id })
    );
    if (!resolved) {
      noteBusy(false);
      return;
    }
    await duplicateIntoTunnel(source, resolved, {
      order: block.order,
      // Only when the resolve had to create the tunnel; sending it with the
      // source's own id (as the fallback did) made the server-side copy a
      // no-op.
      copyTunnelShell: resolved.created,
    });
  };

  /**
   * Hybrid-AHU duplicate — the same `getOrCreateHybridTunnel` resolve the
   * hybrid INSERT runs (legacy forwards the chosen row's addressing into
   * `get_or_create_tunnel_from_index` for the duplicate too,
   * section/model/section.js:452-468), so the copy lands at the row:level,
   * X-Offset and airflow direction the dialog was answered with.
   *
   * In hybrid mode every row creates its own single-section tunnel — there is
   * no "downstream of" picker and the new tunnel is empty, so the section is
   * always its first (`orders[i] || 1`).
   *
   * `copyTunnelConfig` rather than `copyTunnelShell`, which is sent false
   * here. The two differ in what they leave alone, and the hybrid create has
   * ALREADY resolved the placement half through the hybrid rules: the
   * standard `new_tunnel` copy would force the source's base onto a tunnel
   * `AHUTunnels.save` deliberately leaves without one (the first at a level
   * ≥ 2 is carried by the level-1 base below it, models_tunnel.py:1807-1852).
   * The configuration half is not resolved by anything, though — a created
   * tunnel gets the CMS defaults, and `previous_tunnel` inheritance only
   * reaches whichever peer shares its legacy index — so the copy came out
   * with default skins, floor materials and walls however the source tunnel
   * had been set up. `copy_tunnel_config` is the set that carries those over
   * (ahu/duplicate/tunnel_config.py).
   */
  const runHybridDuplicate = async (
    source: IAhuBoardSection,
    target: HybridTunnelTarget
  ) => {
    noteBusy(true);
    // Same pending window as an Add Section insert: this creates a tunnel and
    // then fills it, and until the copy lands that tunnel has no footprint for
    // the next dialog to measure against (pendingSectionWrites.ts). Closed by
    // the poll inside duplicateIntoTunnel, which is where the copy actually
    // appears; the catch below is the backstop for a resolve that never got
    // that far.
    const sectionsWritten = beginSectionWrites();
    try {
      const resolved = await dispatch(getOrCreateHybridTunnel(target, { ahuId: ahu.id }));
      if (!resolved) {
        noteBusy(false);
        sectionsWritten();
        return;
      }
      await duplicateIntoTunnel(source, resolved, {
        order: 1,
        copyTunnelShell: false,
        // Only onto a tunnel this resolve CREATED, the same condition the
        // standard path puts on `new_tunnel`. A hybrid row always asks for a
        // new tunnel, but `tunnel_viewset.create` dedups an address that
        // already exists and hands back the tunnel that holds it — and
        // stamping the source's configuration over a tunnel the user had
        // already set up is not a duplicate, it is an edit of someone else's
        // tunnel. (`getOrCreateHybridTunnel` reports that case as
        // `created: false`, and the server refuses a target that already holds
        // sections, so this holds even when the cached list was stale.)
        copyTunnelConfig: resolved.created,
        onSectionsWritten: sectionsWritten,
      });
    } catch {
      sectionsWritten();
    }
  };

  const insert = (e?: React.MouseEvent) => {
    const binding = bindings[chosen];
    if (!binding || (isDual && binding.active === false)) return;
    const isCtrl = !!e?.ctrlKey;
    if (!isCtrl) {
      dispatch(clearSelection());
      ahuBoardBus.emit('remove:selected:section');
    }
    ahuBoardBus.emit('select:accessory', null);

    const blocks = binding.occupied_tunnel_indexes.map((index, column) => {
      const selIdx = selectedOrder(binding, column);
      // legacy decode: selected option's next sibling value + 2 → here
      // order = selected section index + 2 (Upstream Edge −1 → 1).
      const order = selIdx < 0 ? 1 : selIdx + 2;
      return {
        component_name: request.name,
        component_value: request.value,
        tunnel_index_id: Number(index.id),
        order,
        isCtrlPressed: isCtrl,
      };
    });

    if (request.duplicate && request.sectionId != null) {
      const source = sections.find((s) => s.id === request.sectionId);
      if (source) void runDuplicate(source, blocks[0]);
    } else {
      ahuBoardBus.emit('create:component:any', blocks);
    }
    onClose();
  };

  /** The airflow relation this component's two tunnels are locked into, if any. */
  const dualRelation = isDual ? DUAL_AIRFLOW_RELATION[request.value] : undefined;

  /** A row's stored/defaulted direction for one column, before the pair rule. */
  const rowDirection = (bindingIdx: number, column: number) =>
    hybridDirections[`${bindingIdx}-${column}`] ??
    duplicateSourceDirection(bindingIdx, column) ??
    hybridBindings[bindingIdx]?.targets[column]?.defaultAirflowDirectionId ??
    1;

  /**
   * A row's own airflow direction. Dual rows carry two selects that share one
   * X-Offset input, so the bottom/primary side's direction is what the offset
   * math follows (`getRowAirflowDirectionId`, insert_component.popup.js:231).
   *
   * For a component with a DUAL_AIRFLOW_RELATION the second column is derived
   * from the first rather than stored — that is what makes the rule hold for a
   * row nobody has touched, where each side's `defaultAirflowDirectionId` is
   * the most recent airflow of its OWN level and the two can perfectly well
   * agree (on a fresh hybrid AHU both default Left-to-Right).
   */
  const hybridDirection = (bindingIdx: number, column: number) => {
    if (dualRelation && column === 1) {
      const primary = rowDirection(bindingIdx, 0);
      return dualRelation === 'opposite' ? oppositeDirection(primary) : primary;
    }
    return rowDirection(bindingIdx, column);
  };

  /**
   * `getAddedSectionLength` (insert_component.popup.js:208) — the length of
   * the section about to be inserted, which is how far the "−" default has to
   * reach back so the new tunnel's downstream edge lands exactly on the
   * existing chain's start.
   */
  const addedSectionLength = (): number => {
    if (request.sectionId != null) {
      const source = sections.find((s) => s.id === request.sectionId);
      if (source) return parseFloat(String(source.length ?? 0)) || 0;
    }
    return DEFAULT_SECTION_LENGTH[request.value] ?? DEFAULT_SECTION_LENGTH.plenum;
  };

  /**
   * `getDefaultXOffset` (insert_component.popup.js:262) — the X-Offset a
   * hybrid row defaults to: butt the new tunnel against the rightmost peer's
   * right edge for "+", or the leftmost peer's left edge for "−", reading each
   * peer's own physical span so a mixed-direction chain still resolves.
   *
   * A row:level with no peers yet starts a chain rather than extending one, so
   * there is no end to butt against — it lines up with the START of the chain
   * that carries it instead (`alignmentSpanForNewChain`): flush left for "+",
   * which then grows rightward across that chain, flush right for "−", which
   * grows leftward. Only an AHU with no footprint anywhere still falls back to
   * X = 0, which is where its first chain belongs.
   *
   * What that replaces: an unconditional 0, which put the first tunnel of a new
   * level or row at the absolute origin however far from it the unit actually
   * began — AHU 3972's levels run 78–150, and its new `1:3:1` landed at 0–24,
   * detached from the stack below it (reported 2026-10-07).
   */
  const defaultXOffset = (bindingIdx: number, column: number, sign: XOffsetSign): number => {
    const target = hybridBindings[bindingIdx]?.targets[column];
    if (!target) return 0;
    const peers = tunnels
      .filter((t) => t.row === target.row && t.level === target.level)
      .map(getTunnelPhysicalSpan)
      .sort((a, b) => a.left - b.left);
    const carrier = peers.length ? null : alignmentSpanForNewChain(target, tunnels);
    const extensionPoint = peers.length
      ? sign === 'plus'
        ? peers[peers.length - 1].right
        : peers[0].left
      : carrier
        ? sign === 'plus'
          ? carrier.left
          : carrier.right
        : 0;
    // Dual rows share the bottom side's direction for the offset math.
    const directionId = hybridDirection(bindingIdx, 0);
    return defaultOffsetForNewTunnel(
      addedSectionLength(),
      directionId,
      extensionPoint,
      sign
    );
  };

  /** The UNSIGNED magnitude an X-Offset input shows — typed value if the user
   * has touched it, else the computed default, always to 2dp. */
  const hybridOffsetValue = (bindingIdx: number, column: number): string => {
    const typed = hybridOffsets[`${bindingIdx}-${column}`];
    if (typed != null) return typed;
    return Math.abs(defaultXOffset(bindingIdx, column, xOffsetSign)).toFixed(2);
  };

  /**
   * The signed `x_offset` a row will actually send: its magnitude (typed or
   * defaulted) carrying the sign of its own computed default (`applySign`).
   * Also what the row's sign PREFIX renders, so the number the user reads is
   * the number that gets stored — the prefix used to echo the dialog-wide
   * toggle, which is a direction to extend in and not this row's sign.
   */
  const signedXOffset = (bindingIdx: number, column: number): number =>
    applySign(
      Number(hybridOffsetValue(bindingIdx, column)),
      defaultXOffset(bindingIdx, column, xOffsetSign),
      xOffsetSign
    );

  /**
   * `onXOffsetSignClick` — the toggle is dialog-wide, and switching it
   * recomputes EVERY row (`applyXOffsetSignToAllRows`), discarding typed
   * values. Clearing the override map is that same overwrite.
   */
  const onSignClick = (sign: XOffsetSign) => {
    if (sign === xOffsetSign) return;
    setXOffsetSign(sign);
    setHybridOffsets({});
  };

  /**
   * `onRowAirflowChange` (insert_component.popup.js:361) — the X-Offset
   * default depends on the row's own direction, so flipping DIRECTION
   * recomputes it.
   *
   * The auto-flip: for a row:level whose tunnels hold no sections yet, the
   * default is an alignment with one END of the chain that carries it (X = 0 on
   * an AHU with no footprint at all), and `defaultOffsetForNewTunnel` only
   * resolves that to the tunnel starting AT that end when the sign matches the
   * row's own direction — "+" for Left-to-Right, "−" for Right-to-Left. The
   * toggle defaults to "+", so picking Right-to-Left on a brand-new row would
   * otherwise sit stuck on a default a section length off it. Matching the two
   * up puts a Right-to-Left chain's first tunnel at its inlet, i.e. flush with
   * the downstream end of the chain below, and a Left-to-Right one flush with
   * its upstream end. Deliberately a test for "no SECTIONS at this row:level"
   * rather than "no tunnel there": an emptied AHU keeps neither, but one that
   * still has its starter tunnel is just as much a first section.
   */
  const onRowAirflowChange = (bindingIdx: number, column: number, value: number) => {
    setHybridChosenRow(bindingIdx);
    // Under a DUAL_AIRFLOW_RELATION the pair is one choice, held on column 0:
    // picking a direction for the SECOND tunnel sets the first to whatever
    // keeps the relation (the second is derived back out by hybridDirection).
    // That is the "change one, the other follows" rule, and because the stored
    // value is always the primary's, the X-Offset math below — which reads
    // column 0 — can never be left describing the other tunnel's frame.
    const primaryColumn = dualRelation ? 0 : column;
    const primaryValue =
      dualRelation && column === 1 && dualRelation === 'opposite'
        ? oppositeDirection(value)
        : value;
    setHybridDirections((prev) => ({
      ...prev,
      [`${bindingIdx}-${primaryColumn}`]: primaryValue,
    }));
    const target = hybridBindings[bindingIdx]?.targets[primaryColumn];
    const isFirstSection =
      !!target &&
      !tunnels.some(
        (t) =>
          t.row === target.row &&
          t.level === target.level &&
          (sectionsByTunnel[t.id] ?? []).length > 0
      );
    if (isFirstSection) {
      // `primaryValue`, not the raw `value`: the X-Offset the sign is being
      // matched to is read in the primary tunnel's direction, so flipping the
      // top of a locked pair has to flip the toggle for the bottom.
      const desiredSign: XOffsetSign = primaryValue === 2 ? 'minus' : 'plus';
      if (desiredSign !== xOffsetSign) setXOffsetSign(desiredSign);
    }
    setHybridOffsets({});
  };

  /**
   * Hybrid-mode INSERT (§4 insertComponent). If this is the first section on
   * the whole AHU, PATCH the AHU to hybrid FIRST and await it — the
   * tunnel-create PATCH below unconditionally re-saves the whole AHU row
   * (AHUUnit.save side effects), so creating the tunnel before this lands
   * would clobber is_hybrid back to false.
   *
   * Sent whenever the AHU is empty rather than only when `ahu.is_hybrid` is
   * false: the server clears the flag once the last section goes, so a cached
   * `true` here would skip the PATCH and leave the rebuilt unit flagged
   * Standard in the database.
   */
  const insertHybrid = async (e?: React.MouseEvent) => {
    const row = hybridBindings[hybridChosen];
    // `chainsFresh` — the row's X-Offset and the peer count the create counts
    // `position` from are not trustworthy until the re-read lands.
    if (!row || !chainsFresh) return;
    const isCtrl = !!e?.ctrlKey;
    if (!isCtrl) {
      dispatch(clearSelection());
      ahuBoardBus.emit('remove:selected:section');
    }
    ahuBoardBus.emit('select:accessory', null);

    if (!hasAnySections) {
      try {
        await patchAhuUnit({
          id: ahu.id,
          data: { is_hybrid: true, ...(ahu.anchor == null ? { anchor: 0 } : {}) },
        }).unwrap();
      } catch {
        return;
      }
    }

    // Both tunnels of a dual pair share the one X-Offset input, and the input
    // holds an unsigned magnitude — the sign of the row's own default is
    // applied here (`applySign`), which is what its prefix showed.
    const xOffset = signedXOffset(hybridChosen, 0);

    /**
     * The physical span the new tunnel(s) must cover, resolved ONCE in the
     * primary tunnel's frame (the direction the input is read through, same as
     * `defaultXOffset`). A dual-tunnel component occupies both of its tunnels
     * over the same stretch of X — it is one box spanning them — so each
     * tunnel's own `x_offset` is that span's upstream edge *in its own
     * direction* (`offsetForPhysicalSpan`), which for a counter-flowing pair is
     * the two opposite ends of it.
     *
     * Legacy sent the one stored number to both tunnels ("Both tunnels in a
     * dual pair share the same x_offset", insert_component.popup.js:559), which
     * is only right while the pair runs the same way: a Heat Wheel's second
     * tunnel came out a full section length upstream of the first, drawn beside
     * the wheel's other half instead of over it. Both sections of a dual insert
     * are created at the same length (`DEFAULT_SECTION_LENGTH`), and a tunnel
     * created here holds only that one section, so the span is the same for
     * both.
     */
    const pairSpan = getTunnelPhysicalSpan({
      length: addedSectionLength(),
      x_offset: xOffset,
      airflow_direction_id: hybridDirection(hybridChosen, 0),
    });

    const blocks = row.targets.map((target, column) => {
      const airflowDirectionId = hybridDirection(hybridChosen, column);
      const hybridTarget: HybridTunnelTarget = {
        row: target.row,
        level: target.level,
        position: target.position,
        index_id: target.index_id,
        existingTunnelId: target.existingTunnelId,
        // `addressing` set ⇒ legacy's get_or_create always CREATES; the same
        // legacy index_id repeats across back-to-back peers, so an
        // address lookup would hand back the row's lead tunnel instead.
        alwaysCreate: target.existingTunnelId == null,
        /**
         * The tunnel the new one copies its width/height from. For a duplicate
         * landing at the source's own address that is the SOURCE tunnel, not
         * whichever peer of the chain happens to be newest — the copy should
         * come out the size of the tunnel it was copied from. Any other row
         * keeps the Add Section rule (`peerAt`, the newest peer of the chain
         * being joined).
         */
        peerTunnelId:
          target.existingTunnelId == null &&
          column === 0 &&
          hybridChosen === duplicateSourceRow &&
          duplicateSourceTunnel
            ? duplicateSourceTunnel.id
            : target.peerTunnelId,
        // Column 0 gets `xOffset` back unchanged — the span was built from it.
        x_offset: offsetForPhysicalSpan(pairSpan, airflowDirectionId),
        airflow_direction_id: airflowDirectionId,
      };
      return {
        component_name: request.name,
        component_value: request.value,
        tunnel_index_id: target.index_id,
        // Hybrid rows carry no "downstream of" select — the new tunnel is
        // empty, so the section is always its first (`orders[i] || 1`).
        order: 1,
        isCtrlPressed: isCtrl,
        hybrid: hybridTarget,
      };
    });

    if (request.duplicate && request.sectionId != null) {
      // Duplicate reuses everything above — the row's addressing, its
      // X-Offset and its direction — and then copies the source section into
      // the tunnel that resolve lands on instead of creating a blank one.
      // One target only: a dual-tunnel section cannot be duplicated at all
      // (SectionToolbarChrome refuses it), so `request.type` is always
      // `single_tunnel` here and `row.targets` has one entry.
      //
      // Branching on the REQUEST, not on `duplicateSource`: a duplicate whose
      // source section has since been deleted elsewhere must do nothing, and
      // falling through to the create below would insert a blank section of
      // the source's type instead (`blocks` carry its name and value).
      const target = blocks[0]?.hybrid;
      if (duplicateSource && target) void runHybridDuplicate(duplicateSource, target);
    } else {
      ahuBoardBus.emit('create:component:any', blocks);
    }
    onClose();
  };

  /**
   * `current_configuration` (insert_component.popup.js:178). Legacy
   * configurations come back as "1 x 1", "1 x 2"… and are reformatted to
   * "1 W x 1 H" for display. A hybrid configuration comes back as the bare
   * label "Hybrid" (no " x " substring) and is kept as-is; null — possible
   * when the master row is missing — also reads "Hybrid".
   */
  const currentConfig = (() => {
    const cfg = ahu.tunnel_configuration;
    if (!cfg) return 'Hybrid';
    return cfg.includes(' x ') ? `${cfg.replace(' x ', ' W x ')} H` : cfg;
  })();

  const renderSelect = (binding: InsertionBinding, column: number, bindingIdx: number) => {
    const names = binding.occupied_tunnel_sections[column];
    if (!names || !binding.has_sections) {
      return <span className="upstrem_edge">Upstream Edge</span>;
    }
    return (
      <select
        className="default"
        value={String(selectedOrder(binding, column))}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          // Read the value before the updater: React nulls `currentTarget`
          // once the handler returns, and the updater runs later.
          const order = parseInt(e.currentTarget.value, 10);
          setOrders((prev) => ({ ...prev, [`${bindingIdx}-${column}`]: order }));
        }}
      >
        <option value="-1">Upstream Edge</option>
        {names.map((n, i) => (
          <option key={`${n}-${i}`} value={i}>
            {n}
          </option>
        ))}
      </select>
    );
  };

  /**
   * The "X-OFFSET (in) + / −" column header's sign toggle — one per dialog,
   * rendered inside the header cell (insert_component.popup.tmpl:46-53 single,
   * :139-145 dual). The dual heading has no `.seperator` between the two
   * options; the single one does.
   */
  const renderSignToggle = (withSeparator: boolean) => (
    <span className="row-x-offset-sign-toggle">
      <span
        className={`row-x-offset-sign-option plus${xOffsetSign === 'plus' ? ' active' : ''}`}
        data-sign="plus"
        onClick={(e) => {
          e.stopPropagation();
          onSignClick('plus');
        }}
      >
        +
      </span>
      {withSeparator && <span className="seperator">/</span>}
      <span
        className={`row-x-offset-sign-option minus${xOffsetSign === 'minus' ? ' active' : ''}`}
        data-sign="minus"
        onClick={(e) => {
          e.stopPropagation();
          onSignClick('minus');
        }}
      >
        {'−'}
      </span>
    </span>
  );

  /**
   * A row's X-Offset cell: the sign prefix echoing the header toggle, plus the
   * unsigned magnitude input. `onclick`/`ondblclick` are stopped so a click in
   * the input doesn't bubble to `.ka-row` and re-fire chooseTunnel — and focus
   * explicitly marks the row chosen, or typing in row A's offset while row B
   * is `.choose`n would insert into B with A's values.
   *
   * The box is a `type="text"` carrying legacy's `data-type="number"` /
   * `data-formatter_type="decimal"` / `data-min="0"` markers, which in legacy
   * are what a delegated `numberKeypress` binding reads — but no binding has
   * ever reached THIS dialog, on dev or on legacy. Legacy's AHU-board one
   * (`main.js:92`) is delegated on `#ahu-board-content` and this dialog sits
   * outside it, so the markers were inert and the field took letters there
   * too: 'www' went in, `applySign` read it back as 0, and the section
   * silently inserted at X = 0. Hence the local pair —
   * `numberKeypressFilter` is the shared port of that filter, and
   * `UNSIGNED_DECIMAL` in `onChange` covers what a keypress handler cannot
   * see (paste, drag-drop, autofill).
   *
   * `useBoardNumberInputs` now also covers it, because that hook is
   * page-scoped rather than root-scoped and so reaches BEYOND legacy's board
   * root. That is a deliberate extension, not a parity rule: do not cite this
   * dialog as evidence of what legacy filtered. Both guards are kept — the
   * local pair is the stricter (it also refuses a typed `+`/`-`, the sign
   * being the header toggle's job) and neither interferes with the other, the
   * host filter blocking a keystroke before React sees it and the host
   * backstop only ever rewriting a value `UNSIGNED_DECIMAL` would reject.
   */
  const renderXOffsetCell = (bindingIdx: number) => (
    <li className="medium x-offset-col">
      <span className="row-x-offset-sign-prefix">
        {signedXOffset(bindingIdx, 0) < 0 ? '−' : '+'}
      </span>
      <input
        className="row-x-offset-input"
        type="text"
        data-type="number"
        data-formatter_type="decimal"
        data-step="0.01"
        data-min="0"
        value={hybridOffsetValue(bindingIdx, 0)}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onFocus={() => setHybridChosenRow(bindingIdx)}
        onKeyPress={(e) => numberKeypressFilter(e.nativeEvent, e.currentTarget)}
        onChange={(e) => {
          // Read the value before the updater: React nulls `currentTarget`
          // once the handler returns, and the updater runs later.
          const value = e.currentTarget.value;
          // `numberKeypressFilter` only sees keystrokes; a paste, a drop or an
          // autofill reaches `change` unfiltered, so the whole value is checked
          // again here and a bad one leaves the field on its last good value.
          // Deliberately permissive about half-typed numbers ('', '.', '12.'):
          // they are states you pass through while typing a real one, and
          // `applySign` already reads them back as 0.
          if (!UNSIGNED_DECIMAL.test(value)) return;
          setHybridOffsets((prev) => ({ ...prev, [`${bindingIdx}-0`]: value }));
        }}
        onKeyDown={(e) => {
          // The input is an unsigned magnitude — the sign comes from the
          // header toggle only, so typed '+'/'-' are blocked rather than
          // accepted and stripped. Enter submits (the row is already chosen).
          if (e.key === '+' || e.key === '-') {
            e.preventDefault();
            return;
          }
          if (e.key !== 'Enter') return;
          e.preventDefault();
          void insertHybrid();
        }}
      />
    </li>
  );

  const renderAirflowCell = (
    bindingIdx: number,
    column: number,
    className: string,
    selectClass: string
  ) => (
    <li className={className}>
      <select
        className={selectClass}
        value={hybridDirection(bindingIdx, column)}
        onClick={(e) => e.stopPropagation()}
        onFocus={() => setHybridChosenRow(bindingIdx)}
        onChange={(e) => onRowAirflowChange(bindingIdx, column, Number(e.currentTarget.value))}
      >
        {AIRFLOW_DIRECTIONS.map((d) => (
          <option key={d.id} value={d.id}>
            {isDual ? d.dual : d.single}
          </option>
        ))}
      </select>
    </li>
  );

  return (
    // Class list verbatim from insert_component.popup.tmpl:1 — in particular
    // NO `active`. This popup is visible without it: ahu/styles.css:6642
    // `.location-popup.tunnel-popup { display: block !important }` outranks
    // common.css:27540 `.popup-wrapper { display: none !important }` on
    // specificity, which is exactly why legacy's template never set it.
    //
    // Adding `active` switched on common.css:27554
    // `.popup-wrapper.active .popup-content { animation-name: easePopup }`, and
    // easePopup (:28237) is `scale(0,0) → scale(1,1)` with NO translate — so for
    // its 0.3s it overrode the static `transform: translate(-50%, -30%)` that
    // centres the dialog (ahu/styles.css:6645). Measured: the box rendered at
    // x=662,y=269 for 300ms and then snapped to x=322,y=154 — a 340×115px jump
    // that reads as the dialog closing and reopening. `#insert_component_popup`
    // is the host div BoardContent already renders (legacy's Backbone `el`,
    // body-appended in pre_render), so emitting it again here only duplicated
    // the DOM id.
    <div
      className={`popup-wrapper tunnel-popup location-popup${
        isDual ? ' dual-tunnel ' : ''
      }`}
    >
      <div className="popup-content popup-content-animation">
        <h2 className="popup-heading">
          {/* The heading text is its own span so the h2's `space-between`
              has two flex items to push apart; the toggle is rendered
              only when BOTH modes apply — with a single option there is nothing to
              choose, so it is hidden. */}
          <span className="popup-heading-text">
            {request.duplicate ? ' Duplicate ' : ' ADD '}Section
          </span>
          {showStandardMode && showHybridMode && (
            <span className="tunnel-mode-toggle interactive">
              {showStandardMode && (
                <span
                  className={`tunnel-mode-option${
                    !showHybridMode || mode === 'standard' ? ' active' : ''
                  }`}
                  data-mode="standard"
                  onClick={() => setMode('standard')}
                >
                  Standard
                </span>
              )}
              {showHybridMode && (
                <span
                  className={`tunnel-mode-option${
                    !showStandardMode || mode === 'hybrid' ? ' active' : ''
                  }`}
                  data-mode="hybrid"
                  onClick={() => setMode('hybrid')}
                >
                  Hybrid
                </span>
              )}
            </span>
          )}
        </h2>
        <div className="popup-content-part accss-section">
          <div>
            <span className="section-name">Section :</span>
            <span className="section-tag"> {request.name}</span>
          </div>
          <div>
            <span className="section-name">Section type :</span>
            <span className="section-tag"> {isDual ? 'Dual Tunnel' : 'Single Tunnel'}</span>
            <span className="section-config no-wrap">Current AHU Configuration :</span>
            <span className="section-config-value right-48">{currentConfig}</span>
          </div>
        </div>
        {isHybridMode ? (
          <div className="popup-content-part">
            <div className={isDual ? 'dual-tunnel' : 'single-tunnel'}>
              <div>
                <ul className="list-heading">
                  <li className="small-126">ROW : LEVEL</li>
                  <li className="medium x-offset-col">
                    X-OFFSET (in)
                    {renderSignToggle(!isDual)}
                  </li>
                  {isDual ? (
                    <>
                      <li className="medium">TUNNEL 1 DIRECTION</li>
                      <li className="medium">TUNNEL 2 DIRECTION</li>
                    </>
                  ) : (
                    <li className="long long-padding">DIRECTION</li>
                  )}
                </ul>
                <ul className="list-rows">
                  {hybridBindings.map((row, idx) => (
                    <li
                      key={idx}
                      className={`ka-row${hybridChosen === idx ? ' choose' : ''}${
                        row.isBackToBack ? ' back-to-back-row' : ''
                      }`}
                      data-back-to-back-row={row.targets[0].row}
                      data-back-to-back-level={row.targets[0].level}
                      onClick={() => setHybridChosenRow(idx)}
                      onDoubleClick={(e) => {
                        setHybridChosenRow(idx);
                        void insertHybrid(e);
                      }}
                    >
                      <ul>
                        <li className="small-126" data-id={row.targets[0].index_id}>
                          {row.label}
                        </li>
                        {renderXOffsetCell(idx)}
                        {isDual ? (
                          <>
                            {renderAirflowCell(idx, 0, 'medium', 'row-airflow-select-1')}
                            {renderAirflowCell(idx, 1, 'medium', 'row-airflow-select-2')}
                            <li data-id={row.targets[1].index_id} style={{ display: 'none' }} />
                          </>
                        ) : (
                          renderAirflowCell(idx, 0, 'long', 'row-airflow-select')
                        )}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <div className="popup-content-part">
            <div className={isDual ? 'dual-tunnel' : 'single-tunnel'}>
              <div>
                <ul className="list-heading">
                  <li className="small-126">ADD IN TUNNEL</li>
                  <li className="medium">ADD DOWNSTREAM OF</li>
                  {isDual && (
                    <>
                      <li className="small small-to">- to -</li>
                      <li className="small">TUNNEL</li>
                      <li className="medium downstream-of-secondary">DOWNSTREAM OF</li>
                    </>
                  )}
                  <li className="long">RESULTING AHU CONFIGURATION</li>
                </ul>
                <ul className="list-rows">
                  {bindings.map((binding, idx) => (
                    <li
                      key={idx}
                      className={`ka-row${chosen === idx ? ' choose' : ''}${
                        isDual && binding.active === false ? ' non-clickable' : ''
                      }`}
                      onClick={() => setChosen(idx)}
                      onDoubleClick={(e) => {
                        setChosen(idx);
                        insert(e);
                      }}
                    >
                      <ul>
                        <li
                          className="small-126"
                          data-id={binding.occupied_tunnel_indexes[0]?.id}
                        >
                          {String(binding.occupied_tunnel_indexes[0]?.name ?? '')}
                        </li>
                        <li className="medium">{renderSelect(binding, 0, idx)}</li>
                        {isDual && (
                          <>
                            <li className="small small-to" />
                            <li
                              className="small"
                              data-id={binding.occupied_tunnel_indexes[1]?.id}
                            >
                              {String(binding.occupied_tunnel_indexes[1]?.name ?? '')}
                            </li>
                            <li className="medium downstream-of-secondary">{renderSelect(binding, 1, idx)}</li>
                          </>
                        )}
                        <li className="long">
                          {String(binding.resulting_configuration?.config_name ?? '')}
                        </li>
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
        <div className="popup-control-buttons">
          <button
            className={`btn insert${
              (isHybridMode ? hybridBindings.length && chainsFresh : bindings.length)
                ? ''
                : ' non-clickable'
            }`}
            autoFocus
            onClick={(e) => (isHybridMode ? void insertHybrid(e) : insert(e))}
          >
            INSERT
          </button>
          <button className="btn cancel" onClick={onClose}>
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
};

export default InsertSectionPopup;
